import React, { useCallback, useEffect, useState } from 'react';
import {
    ActionsResponse, GateActions, OutcomesResponse, PipelineResponse, ReviewGate, caseWorkflowService
} from '../../services/caseWorkflowService';
import { KeySummary } from '../ExecutionStats/useExecutionSummary';
import { PanelError } from '../Benchmark/BenchmarkPrimitives';
import DecisionsWaiting from './DecisionsWaiting';
import ReadyQueues from './ReadyQueues';
import OutcomesStrip from './OutcomesStrip';
import IntegrityWarnings from './IntegrityWarnings';
import PipelineMap from './PipelineMap';
import { OUTCOME_WINDOW_DAYS } from './projectOutcomesVocabulary';

/**
 * The project's case activity: what is waiting, what is queued, what was decided,
 * and the pipeline it all sits on.
 *
 * Read by the page rather than the view, because more than the Status tab needs
 * it: the catalog on the Workflows tab is ordered by the pipeline, and the tab
 * bar counts the decisions waiting — neither can wait for the Status tab to be
 * opened.
 */
export interface ProjectBusinessData {
    actions: ActionsResponse | null;
    outcomes: OutcomesResponse | null;
    contractGates: ReviewGate[];
    /** Null when the pipeline could not be read; the tiles stand in for it. */
    pipeline: PipelineResponse | null;
    error: string | null;
    retry: () => void;
}

/** Reads the project's case activity, again whenever refreshKey is bumped. */
export const useProjectBusinessData = (projectId: string | undefined, refreshKey: number): ProjectBusinessData => {
    const [actions, setActions] = useState<ActionsResponse | null>(null);
    const [outcomes, setOutcomes] = useState<OutcomesResponse | null>(null);
    const [contractGates, setContractGates] = useState<ReviewGate[]>([]);
    const [pipeline, setPipeline] = useState<PipelineResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!projectId) return;
        let cancelled = false;
        setError(null);
        const since = new Date(Date.now() - OUTCOME_WINDOW_DAYS * 24 * 36e5).toISOString();
        Promise.all([
            caseWorkflowService.getActions(projectId),
            caseWorkflowService.getOutcomes(projectId, since),
            caseWorkflowService.getContract(),
            // Optional: a control plane without /pipeline, or a failed read, leaves
            // the page on the decision and queue tiles rather than on an error.
            caseWorkflowService.getPipeline(projectId).catch(() => null)
        ])
            .then(([actionsRes, outcomesRes, contract, pipelineRes]) => {
                if (cancelled) return;
                setActions(actionsRes);
                setOutcomes(outcomesRes);
                setContractGates(contract.reviewGates ?? []);
                setPipeline(pipelineRes);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.message || 'Could not load case activity.');
            });
        return () => { cancelled = true; };
    }, [projectId, refreshKey, attempt]);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);
    return { actions, outcomes, contractGates, pipeline, error, retry };
};

interface ProjectBusinessViewProps {
    data: ProjectBusinessData;
    onOpenGate: (gate: GateActions) => void;
    onOpenWorkflow: (campaignId: string) => void;
    onOpenCaseOperations: () => void;
    /** Each workflow's run statistics, as the page collected them; marks broken runs on the map. */
    runStatsById?: Record<string, KeySummary | null>;
}

/**
 * The project's business state: what is waiting on a person, what is ready for
 * the next workflow, and what the decisions decided.
 *
 * This is the page's default tab because the pipeline is gated by human
 * decisions — triage, EDD referral, EDD sign-off — and a run that "succeeded"
 * says nothing about whether its case is waiting on one. Run health stays on
 * the Workflows tab as the operations signal.
 */
const ProjectBusinessView: React.FC<ProjectBusinessViewProps> = ({
    data, onOpenGate, onOpenWorkflow, onOpenCaseOperations, runStatsById
}) => {
    const { actions, outcomes, contractGates, pipeline, error, retry } = data;

    const openGateByCode = useCallback((code: string) => {
        const gate = actions?.gates.find((g) => g.code === code);
        if (gate && gate.openCases > 0) onOpenGate(gate);
    }, [actions, onOpenGate]);

    if (error) {
        return <PanelError message={error} onRetry={retry} />;
    }
    if (!actions || !outcomes) {
        return (
            <div aria-busy="true" className="space-y-3">
                <div className="h-28 rounded-xl border border-line bg-surface/50 animate-pulse" />
                <div className="h-20 rounded-xl border border-line bg-surface/50 animate-pulse" />
            </div>
        );
    }

    const gateLabels = Object.fromEntries(actions.gates.map((g) => [g.code, g.label]));
    return (
        <div className="space-y-4">
            <IntegrityWarnings warnings={actions.warnings} />
            {pipeline && pipeline.edges.length > 0 ? (
                <PipelineMap
                    pipeline={pipeline}
                    actions={actions}
                    runStatsById={runStatsById}
                    onOpenWorkflow={onOpenWorkflow}
                    onOpenGate={onOpenGate}
                />
            ) : (
                <>
                    <DecisionsWaiting gates={actions.gates} contractGates={contractGates} onOpenGate={onOpenGate} />
                    <ReadyQueues
                        queues={actions.queues}
                        onOpenWorkflow={onOpenWorkflow}
                        onOpenGate={openGateByCode}
                        gateLabels={gateLabels}
                    />
                </>
            )}
            <OutcomesStrip
                outcomes={outcomes}
                windowDays={OUTCOME_WINDOW_DAYS}
                onOpenCaseOperations={onOpenCaseOperations}
            />
        </div>
    );
};

export default ProjectBusinessView;
